import { useMemo } from 'react';

const safeGetTests = (collection, id) => {
    if (!collection || id == null) return [];
    return collection[id] || [];
};

const safeGetAssessments = (collection, id) => {
    if (!collection || id == null) return [];
    return collection[id] || [];
};

export const useCurriculumProgression = ({ course, tests, assessments }) => {
    return useMemo(() => {
        const moduleStateMap = new Map();
        const lessonStateMap = new Map();
        const topicStateMap = new Map();
        const contentStateMap = new Map();

        const modules = (course?.modules || []).map((module, moduleIdx) => {
            const currentModuleTests = safeGetTests(tests?.moduleTests, module.id);

            const lessonStates = (module.lessons || []).map((lesson, lessonIdx) => {
                const topicStates = (lesson.topics || []).map((topic, topicIdx) => {
                    const contents = topic.content || [];

                    const firstIncompleteContentIdx = contents.findIndex(content => content.status !== 'completed');
                    const contentUnlockIndex = firstIncompleteContentIdx === -1 ? contents.length - 1 : firstIncompleteContentIdx;

                    const contentStates = contents.map((content, contentIdx) => {
                        const isCompleted = content.status === 'completed';
                        const isUnlocked = contentIdx <= contentUnlockIndex;

                        const contentTests = safeGetTests(tests?.contentTests, content.id);
                        const contentTestCompleted = contentTests.length === 0 || contentTests.every(test => test.isCompleted);

                        const contentAssessments = safeGetAssessments(assessments?.contents, content.id);
                        const contentAssessmentCompleted = contentAssessments.length === 0 || contentAssessments.every(assessment => assessment.isCompleted);

                        const contentState = {
                            contentId: content.id,
                            topicId: topic.id,
                            lessonId: lesson.id,
                            moduleId: module.id,
                            index: contentIdx,
                            isCompleted,
                            isUnlocked,
                            contentTest: {
                                isUnlocked: isCompleted,
                                isCompleted: contentTestCompleted,
                                lockReason: isCompleted ? '' : 'Complete this content first',
                            },
                            contentAssessment: {
                                isUnlocked: isCompleted,
                                isCompleted: contentAssessmentCompleted,
                                lockReason: isCompleted ? '' : 'Complete this content first',
                            },
                        };
                        contentStateMap.set(content.id, contentState);
                        return contentState;
                    });

                    const topicContentCompleted = contentStates.length === 0 || contentStates.every(contentState => contentState.isCompleted);
                    const topicTests = safeGetTests(tests?.topicTests, topic.id);
                    const topicExamCompleted = topicTests.length === 0 || topicTests.every(test => test.isCompleted);
                    const topicExamUnlocked = topicContentCompleted && topicTests.some(test => !test.isCompleted);

                    const topicAssessments = safeGetAssessments(assessments?.topics, topic.id);
                    const topicAssessmentCompleted = topicAssessments.length === 0 || topicAssessments.every(assessment => assessment.isCompleted);

                    const allContentTestsCompleted = contentStates.every(contentState => {
                        const contentTests = safeGetTests(tests?.contentTests, contentState.contentId);
                        return contentTests.length === 0 || contentTests.every(test => test.isCompleted);
                    });

                    const allContentAssessmentsCompleted = contentStates.every(contentState => {
                        const contentAssessments = safeGetAssessments(assessments?.contents, contentState.contentId);
                        return contentAssessments.length === 0 || contentAssessments.every(assessment => assessment.isCompleted);
                    });

                    const topicFullyCompleted = topicContentCompleted && topicExamCompleted && allContentTestsCompleted && allContentAssessmentsCompleted;

                    const topicState = {
                        topicId: topic.id,
                        lessonId: lesson.id,
                        moduleId: module.id,
                        index: topicIdx,
                        isUnlocked: false,
                        isFullyCompleted: topicFullyCompleted,
                        isContentCompleted: topicContentCompleted,
                        contentStates,
                        topicExam: {
                            isUnlocked: topicExamUnlocked,
                            isCompleted: topicExamCompleted,
                            lockReason: topicContentCompleted ? '' : 'Complete topic content first',
                        },
                        topicAssessment: {
                            isUnlocked: topicContentCompleted,
                            isCompleted: topicAssessmentCompleted,
                            lockReason: topicContentCompleted ? '' : 'Complete topic content first',
                        },
                        lockReason: '',
                    };

                    topicStateMap.set(topic.id, topicState);
                    return topicState;
                });

                const firstIncompleteTopicIdx = topicStates.findIndex(topicState => !topicState.isFullyCompleted);
                const topicUnlockIndex = firstIncompleteTopicIdx === -1 ? topicStates.length - 1 : firstIncompleteTopicIdx;
                topicStates.forEach((topicState, idx) => {
                    const unlocked = idx <= topicUnlockIndex;
                    topicState.isUnlocked = unlocked;
                    topicState.lockReason = unlocked ? '' : 'Complete previous topics before unlocking this one';
                });

                const topicsCompleted = topicStates.every(topicState => topicState.isFullyCompleted);
                const lessonTests = safeGetTests(tests?.lessonTests, lesson.id);
                const lessonExamCompleted = lessonTests.length === 0 || lessonTests.every(test => test.isCompleted);
                const lessonExamUnlocked = topicsCompleted && lessonTests.some(test => !test.isCompleted);
                
                // Check for lesson assessments
                const lessonAssessments = safeGetAssessments(assessments?.lessons, lesson.id);
                const lessonAssessmentCompleted = lessonAssessments.length === 0 || lessonAssessments.every(assessment => assessment.isCompleted);

                const lessonState = {
                    lessonId: lesson.id,
                    moduleId: module.id,
                    index: lessonIdx,
                    isUnlocked: false,
                    isCompleted: topicsCompleted && lessonExamCompleted && lessonAssessmentCompleted,
                    topicStates,
                    lessonExam: {
                        isUnlocked: lessonExamUnlocked,
                        isCompleted: lessonExamCompleted,
                        lockReason: topicsCompleted ? '' : 'Finish all topics and topic exams first',
                    },
                    lessonAssessment: {
                        isUnlocked: topicsCompleted,
                        isCompleted: lessonAssessmentCompleted,
                        lockReason: topicsCompleted ? '' : 'Finish all topics and topic exams first',
                    },
                };

                lessonStateMap.set(lesson.id, lessonState);
                return lessonState;
            });

            const firstIncompleteLessonIdx = lessonStates.findIndex(lessonState => !lessonState.isCompleted);
            const lessonUnlockIndex = firstIncompleteLessonIdx === -1 ? lessonStates.length - 1 : firstIncompleteLessonIdx;
            lessonStates.forEach((lessonState, idx) => {
                const unlocked = idx <= lessonUnlockIndex;
                lessonState.isUnlocked = unlocked;
                lessonState.lockReason = unlocked ? '' : 'Complete previous lessons before unlocking this one';
            });

            const lessonsCompleted = lessonStates.every(lessonState => lessonState.isCompleted);
            const moduleTestCompleted = currentModuleTests.length === 0 || currentModuleTests.every(test => test.isCompleted);
            const moduleTestUnlocked = lessonsCompleted && currentModuleTests.some(test => !test.isCompleted);
            
            // Check for module assessments
            const moduleAssessments = safeGetAssessments(assessments?.modules, module.id);
            const moduleAssessmentCompleted = moduleAssessments.length === 0 || moduleAssessments.every(assessment => assessment.isCompleted);

            const moduleState = {
                moduleId: module.id,
                index: moduleIdx,
                isUnlocked: false,
                isCompleted: lessonsCompleted && moduleTestCompleted && moduleAssessmentCompleted,
                lessonStates,
                moduleTest: {
                    isUnlocked: moduleTestUnlocked,
                    isCompleted: moduleTestCompleted,
                    lockReason: lessonsCompleted ? '' : 'Finish all lessons and their exams first',
                },
                moduleAssessment: {
                    isUnlocked: lessonsCompleted,
                    isCompleted: moduleAssessmentCompleted,
                    lockReason: lessonsCompleted ? '' : 'Finish all lessons and their exams first',
                },
                lockReason: '',
            };

            moduleStateMap.set(module.id, moduleState);
            return moduleState;
        });

        const firstIncompleteModuleIdx = modules.findIndex(moduleState => !moduleState.isCompleted);
        const moduleUnlockIndex = firstIncompleteModuleIdx === -1 ? modules.length - 1 : firstIncompleteModuleIdx;
        modules.forEach((moduleState, idx) => {
            const unlocked = idx <= moduleUnlockIndex;
            moduleState.isUnlocked = unlocked;
            moduleState.lockReason = unlocked ? '' : 'Complete previous modules before unlocking this module';
        });

        return {
            modules,
            moduleStateMap,
            lessonStateMap,
            topicStateMap,
            contentStateMap,
        };
    }, [course, tests, assessments]);
};
